/**
 * useDeliveryForm Custom Hook
 * Manages delivery form state with auto-fill capabilities
 * @author SAUBHAGYA Development Team
 */

import { useState, useCallback } from 'react';
import type { DeliveryFormData } from '../utils/orderToDeliveryMapper';

export interface UseDeliveryFormReturn {
  formData: DeliveryFormData;
  isLoading: boolean;
  error: string | null;
  autoFilledFields: string[];
  isAutoFilled: boolean;
  updateField: (field: keyof DeliveryFormData, value: any) => void;
  updateMultipleFields: (updates: Partial<DeliveryFormData>) => void;
  reset: () => void;
  setAutoFilledData: (data: Partial<DeliveryFormData>, initialFormData: DeliveryFormData) => void;
}

const DEFAULT_FORM_DATA: DeliveryFormData = {
  // REQUIRED: Drop/Delivery Details
  dropAddress: '',
  dropContactName: '',
  dropContactPhone: '',

  // OPTIONAL: Vehicle & Driver Details
  vehicleNumber: undefined,
  driverName: undefined,
  driverPhone: undefined,

  // OPTIONAL: Additional Information
  additionalNotes: undefined,

  // Pickup details (optional - will be auto-filled from mapper)
  pickupAddress: 'SAUBHAGYA Manufacturing Unit, Biogas Production Facility',
  pickupLatitude: undefined,
  pickupLongitude: undefined,
  pickupContactName: 'Warehouse Manager',
  pickupContactPhone: '', // Optional - no default
  requestedPickupTime: undefined,
  productType: '',
  quantity: 0,
  productDescription: undefined,
  unit: 'Kilograms',
  weightKg: 0,
  priority: 'Medium',
  estimatedAmount: 0,
  markAsUrgent: false,

  // Source tracking
  assignedFrom: 'UrjaVyapar',
};

/**
 * Custom hook for delivery form state management
 */
export function useDeliveryForm(): UseDeliveryFormReturn {
  const [formData, setFormData] = useState<DeliveryFormData>(DEFAULT_FORM_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoFilledFields, setAutoFilledFields] = useState<string[]>([]);
  const [isAutoFilled, setIsAutoFilled] = useState(false);

  /**
   * Update a single field in form
   */
  const updateField = useCallback(
    (field: keyof DeliveryFormData, value: any) => {
      try {
        setFormData(prev => ({
          ...prev,
          [field]: value,
        }));
        setError(null);
      } catch (err) {
        console.error(`[useDeliveryForm] Error updating field ${field}:`, err);
        setError(`Failed to update ${field}`);
      }
    },
    []
  );

  /**
   * Update multiple fields at once
   */
  const updateMultipleFields = useCallback(
    (updates: Partial<DeliveryFormData>) => {
      try {
        setFormData(prev => ({
          ...prev,
          ...updates,
        }));
        setError(null);
      } catch (err) {
        console.error('[useDeliveryForm] Error updating multiple fields:', err);
        setError('Failed to update form fields');
      }
    },
    []
  );

  /**
   * Reset form to default state
   */
  const reset = useCallback(() => {
    setFormData(DEFAULT_FORM_DATA);
    setAutoFilledFields([]);
    setIsAutoFilled(false);
    setError(null);
  }, []);

  /**
   * Set auto-filled data and track which fields were auto-filled
   */
  const setAutoFilledData = useCallback(
    (data: Partial<DeliveryFormData>, initialFormData: DeliveryFormData) => {
      try {
        const mergedData = {
          ...initialFormData,
          ...data,
        };

        // Track which fields were auto-filled (non-empty values only)
        const filledFields = Object.keys(data).filter(
          key => {
            const value = data[key as keyof DeliveryFormData];
            return value !== undefined && value !== '' && value !== null;
          }
        );

        setFormData(mergedData);
        setAutoFilledFields(filledFields);
        setIsAutoFilled(true);
        setError(null);

        console.log('[useDeliveryForm] Auto-fill completed:', {
          filledFieldsCount: filledFields.length,
          filledFields,
        });
      } catch (err) {
        console.error('[useDeliveryForm] Error setting auto-filled data:', err);
        setError('Failed to auto-fill form');
      }
    },
    []
  );

  return {
    formData,
    isLoading,
    error,
    autoFilledFields,
    isAutoFilled,
    updateField,
    updateMultipleFields,
    reset,
    setAutoFilledData,
  };
}
